import { Fragment, type ReactNode } from 'react';
import type { Components } from 'react-markdown';
import { formatTableCell } from './answerWidgetFormat.ts';
import { parseAnswerWidgetPayload } from './answerWidgetPayload.ts';
import type { AnswerWidgetRenderer } from './answerWidgetRenderer.ts';
import { widgetIdFromPre } from './widgetIdFromPre.ts';

const CELL_SEPARATOR = ' | ';
const EMPTY_CELL = 'N/A';

/**
 * react-markdown components for copying an answer: a widget marker becomes its title and one line per
 * row with every row, so a paste carries the data rather than the raw fence. Lines rather than an HTML
 * table, because editors without tables (Slack) drop the cell boundaries and glue the values together.
 * A marker whose payload is malformed or whose id is invalid copies as the error notice, and a marker with
 * no widget copies as nothing, as it renders. A widget with no rows copies as its title and the empty notice.
 * `renderWidget` writes the widget kinds a consumer brings itself and returns `undefined` for the rest.
 */
export function answerWidgetClipboardComponents(
  widgets: Record<string, unknown> = {},
  {
    errorNotice = '',
    emptyNotice = '',
    renderWidget,
  }: { errorNotice?: string; emptyNotice?: string; renderWidget?: AnswerWidgetRenderer | undefined } = {}
): Components {
  return {
    pre: ({ children }: { children?: ReactNode }) => {
      const marker = widgetIdFromPre(children);
      if (marker === null) return <pre>{children}</pre>;

      const payload = marker.id === null ? null : widgets[marker.id];
      if (payload === undefined) return null;

      const customWidget = renderWidget?.(payload);
      if (customWidget !== undefined) return customWidget;

      const parsed = parseAnswerWidgetPayload(payload);
      if (!parsed.ok) return <p>{errorNotice}</p>;

      const { title, columns, rows, granularity, timeKey } = parsed.value;
      if (rows.length === 0) {
        return (
          <div>
            <p>
              <strong>{title}</strong>
            </p>
            <p>{emptyNotice}</p>
          </div>
        );
      }

      const lines = [
        columns.map(column => column.label),
        ...rows.map(row =>
          columns.map(
            column => formatTableCell(row[column.key], column, granularity, column.key === timeKey) || EMPTY_CELL
          )
        ),
      ].map(cells => cells.join(CELL_SEPARATOR));

      return (
        <div>
          <p>
            <strong>{title}</strong>
          </p>
          <p>
            {lines.map((line, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static markup, rows have no identity
              <Fragment key={index}>
                {index > 0 && <br />}
                {line}
              </Fragment>
            ))}
          </p>
        </div>
      );
    },
  };
}
